/**
 * Settings store for managing app configuration
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { DEFAULT_MODEL, AVAILABLE_MODELS } from '../types/models';

// MCP Server configuration types
interface MCPServerConfig {
  command: string;
  args: string[];
  env?: Record<string, string>;
}

// Avatar settings types
interface AvatarSettings {
  selectedAvatar: string;
  backgroundType: 'color' | 'image';
  selectedBackground: string;
}

interface SettingsState {
  // Model configuration
  currentModel: string;
  
  // UI preferences
  theme: 'light' | 'dark' | 'system';
  sidebarCollapsed: boolean;
  
  // API configuration
  apiEndpoint: string;
  
  // MCP server configuration
  mcpServers: Record<string, MCPServerConfig>;
  
  // Avatar settings
  avatarSettings: AvatarSettings | null;
  avatarEnabled: boolean;

  // Notification settings
  notificationSoundEnabled: boolean;
  notificationPushEnabled: boolean;
  notificationVolume: number;

  // Actions
  setCurrentModel: (model: string) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setApiEndpoint: (endpoint: string) => void;
  setMCPServers: (servers: Record<string, MCPServerConfig>) => void;
  addMCPServer: (name: string, config: MCPServerConfig) => void;
  removeMCPServer: (name: string) => void;
  updateMCPServer: (name: string, config: MCPServerConfig) => void;
  setAvatarSettings: (settings: AvatarSettings | null) => void;
  setAvatarEnabled: (enabled: boolean) => void;
  setNotificationSoundEnabled: (enabled: boolean) => void;
  setNotificationPushEnabled: (enabled: boolean) => void;
  setNotificationVolume: (volume: number) => void;

  // Computed getters
  getCurrentModel: () => string;
  getMCPServers: () => Record<string, MCPServerConfig>;
  getAvatarSettings: () => AvatarSettings | null;
}

export const useSettingsStore = create<SettingsState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        currentModel: DEFAULT_MODEL,
        theme: 'system',
        sidebarCollapsed: false,
        apiEndpoint: (import.meta.env.VITE_API_URL || '/api') === '/api' ? '/api' : `${import.meta.env.VITE_API_URL}/api`,
        mcpServers: {},
        avatarSettings: null,
        avatarEnabled: false,
        notificationSoundEnabled: true,
        notificationPushEnabled: false,
        notificationVolume: 0.5,

        // Actions
        setCurrentModel: (model) => {
          set({ currentModel: model }, false, 'setCurrentModel');
        },
        
        setTheme: (theme) => {
          set({ theme }, false, 'setTheme');
        },
        
        setSidebarCollapsed: (collapsed) => {
          set({ sidebarCollapsed: collapsed }, false, 'setSidebarCollapsed');
        },
        
        setApiEndpoint: (endpoint) => {
          set({ apiEndpoint: endpoint }, false, 'setApiEndpoint');
        },
        
        setMCPServers: (servers) => {
          set({ mcpServers: servers }, false, 'setMCPServers');
        },
        
        addMCPServer: (name, config) => {
          const current = get().mcpServers;
          set({ 
            mcpServers: { ...current, [name]: config } 
          }, false, 'addMCPServer');
        },
        
        removeMCPServer: (name) => {
          const current = get().mcpServers;
          const { [name]: removed, ...remaining } = current;
          set({ mcpServers: remaining }, false, 'removeMCPServer');
        },
        
        updateMCPServer: (name, config) => {
          const current = get().mcpServers;
          set({ 
            mcpServers: { ...current, [name]: config } 
          }, false, 'updateMCPServer');
        },
        
        setAvatarSettings: (settings) => {
          set({ avatarSettings: settings }, false, 'setAvatarSettings');
        },
        
        setAvatarEnabled: (enabled) => {
          set({ avatarEnabled: enabled }, false, 'setAvatarEnabled');
        },

        setNotificationSoundEnabled: (enabled) => {
          set({ notificationSoundEnabled: enabled }, false, 'setNotificationSoundEnabled');
        },

        setNotificationPushEnabled: (enabled) => {
          set({ notificationPushEnabled: enabled }, false, 'setNotificationPushEnabled');
        },

        setNotificationVolume: (volume) => {
          set({ notificationVolume: Math.max(0, Math.min(1, volume)) }, false, 'setNotificationVolume');
        },

        // Computed getters
        getCurrentModel: () => {
          return get().currentModel;
        },
        
        getMCPServers: () => {
          return get().mcpServers;
        },
        
        getAvatarSettings: () => {
          return get().avatarSettings;
        },
      }),
      {
        name: 'agentic-ivy-settings',
        // Only persist certain keys
        partialize: (state) => ({
          currentModel: state.currentModel,
          theme: state.theme,
          sidebarCollapsed: state.sidebarCollapsed,
          apiEndpoint: state.apiEndpoint,
          mcpServers: state.mcpServers,
          avatarSettings: state.avatarSettings,
          avatarEnabled: state.avatarEnabled,
          notificationSoundEnabled: state.notificationSoundEnabled,
          notificationPushEnabled: state.notificationPushEnabled,
          notificationVolume: state.notificationVolume,
        }),
        // Migration logic to handle old model values
        migrate: (persistedState: any, _version: number) => {
          // Check if the persisted model is valid (exists in AVAILABLE_MODELS)
          if (persistedState?.currentModel) {
            const isValidModel = AVAILABLE_MODELS.some(m => m.id === persistedState.currentModel);

            // If invalid model (e.g., old Claude model), reset to default Gemini model
            if (!isValidModel) {
              console.warn(`⚠️ Invalid model "${persistedState.currentModel}" found in localStorage, resetting to ${DEFAULT_MODEL}`);
              persistedState.currentModel = DEFAULT_MODEL;
            }
          }

          return persistedState;
        },
        version: 1, // Increment this when you need to force migration
      }
    ),
    {
      name: 'settings-store',
    }
  )
);